import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import type { AppDispatch } from "_shared/_stores/store-types";
import type { RootState } from "_shared/_stores/root-reducer";
import Guid from "_shared/guids/guid";
import { individualsApi } from "marketplace/individuals/_api/individual-api";
import { getIndividualUnavailabilityByUserIds } from "marketplace/individuals/_stores/individuals-redux-slice";
import Individual from "marketplace/individuals/individual";
import { type PermissionKind, resourceFilterFor } from "permissions/_api/permission-kinds-api-service";
import { canNameTargetsOf, permissionTargetsApi } from "permissions/_api/permission-targets-api-service";
import PermissionFilterParameters from "permissions/_api/queries/filtering/permission-filter-parameters";
import PermissionOrderParameters, { PermissionOrderField } from "permissions/_api/queries/ordering/permission-order-params";
import PermissionQuery, { PermissionView } from "permissions/_api/queries/permission-query";
import { permissionsApi } from "permissions/_api/permissions-api-service";
import { type GrantExportNames, targetKey } from "permissions/_views/export/grant-export-model";
import { kindOfResource } from "permissions/_views/grant-row/grant-row-model";
import { targetIdOf } from "permissions/_views/people/person-record-grants-model";
import { recordFilter } from "permissions/_views/records/use-record-grants";
import { personNameStates } from "permissions/_views/targets/person-name-states";
import { type NameState, nameStates } from "permissions/_views/targets/permission-target-state";
import Permission from "permissions/permission";

/**
 * The reads behind the CSV export (#6786): every page of the grants a page lists, then the names of
 * their people and records, through the same endpoints the page draws them from.
 *
 * The file is built here in the browser, not by a new server endpoint. `GET /permissions` already
 * answers only what the caller may read (their own grants, or with Member Permissions their
 * organization's), and each name comes from the same `ByIdsResult` reads the rows use, so the file
 * holds what the pages would show across all their pages and nothing more: a record the viewer may
 * not read is "No access to this …" in the file as on the screen. The decision is recorded in
 * `docs/design/permissions/PERMISSIONS_UI_DESIGN.md`, "CSV export".
 */

/** The largest page `GET /permissions` serves. */
export const EXPORT_PAGE_SIZE = 100;

/** How many ids one name lookup asks about. */
const LOOKUP_SIZE = 100;

/** Which of a person's grants the file holds, from the page's state filter. */
export type PersonExportStates = "live" | "removed" | "both";

/** What a page exports: one person's grants, one record's, or one kind's. */
export type GrantExportScope =
  | Readonly<{ subject: "person"; userId: Guid; states: PersonExportStates }>
  | Readonly<{ subject: "record"; kind: PermissionKind; recordId: Guid }>
  | Readonly<{ subject: "kind"; kind: PermissionKind }>;

/**
 * The filter each page's file is read with. It always includes removed grants, whatever the page
 * lists: the pages are offsets into the result, so a grant removed while the file is read would leave
 * a live-only result, move every later grant up one place, and drop the first grant of the next page
 * unread. Read with removed grants, a removal changes no grant's place; which states the file keeps
 * is decided by `keptInExport` afterwards.
 */
export function grantExportFilter(scope: GrantExportScope): PermissionFilterParameters {
  switch (scope.subject) {
    case "person":
      return new PermissionFilterParameters({ granteeUserIds: [scope.userId], showDisabled: true });
    case "record":
      return new PermissionFilterParameters({ showDisabled: true, ...recordFilter(scope.kind, scope.recordId) });
    case "kind":
      return new PermissionFilterParameters({ resource: resourceFilterFor(scope.kind), showDisabled: true });
  }
}

/**
 * Whether the file holds a grant, by the states its page lists. The person page's state filter picks
 * live, removed or both; the record page lists live and removed grants together, and so does its
 * file; the kind page lists live grants only.
 */
export function keptInExport(scope: GrantExportScope, grant: Permission): boolean {
  switch (scope.subject) {
    case "person":
      return scope.states === "both" || (scope.states === "removed") === grant.isDisabled;
    case "record":
      return true;
    case "kind":
      return !grant.isDisabled;
  }
}

/**
 * One page of the file's grants, flat so each carries the parents its Source names. Oldest first, so
 * a grant made while the file is read lands on the last page instead of pushing one off an earlier.
 */
export function grantExportQuery(scope: GrantExportScope, pageIndex: number): PermissionQuery {
  return new PermissionQuery({
    permissionView: PermissionView.Flat,
    paginationParameters: PaginationParameters.create({ pageIndex, pageSize: EXPORT_PAGE_SIZE }),
    orderParameters: new PermissionOrderParameters({ orderBy: PermissionOrderField.CreatedDate, orderDir: OrderDirection.Ascending }),
    filterParameters: grantExportFilter(scope),
  });
}

function chunks<T>(items: readonly T[], size: number): T[][] {
  const result: T[][] = [];
  for (let start = 0; start < items.length; start += size) result.push(items.slice(start, start + size));
  return result;
}

/** Progress, for the button: how many grants are read, of how many. */
export type GrantExportProgress = (read: number, total: number) => void;

/**
 * Every page, until the server's `totalPages`. Unsubscribed and refetched, so the file is the
 * server's answer now and leaves nothing in the cache. Oldest first and with removed grants
 * included, a grant made while the file is read lands on the last page and a removal moves nothing,
 * so no grant is skipped. A grant deleted outright between pages would still move the later ones up
 * and one would be read twice; it is kept once.
 */
async function readEveryGrant(dispatch: AppDispatch, scope: GrantExportScope, onProgress?: GrantExportProgress): Promise<Permission[]> {
  const byId = new Map<string, Permission>();
  let pageIndex = 0;
  let totalPages = 1;
  while (pageIndex < totalPages) {
    const page = await dispatch(
      permissionsApi.endpoints.getPermissions.initiate(
        { query: grantExportQuery(scope, pageIndex) },
        { subscribe: false, forceRefetch: true },
      ),
    ).unwrap();
    for (const grant of Permission.fromJSONArray(page.data)) byId.set(grant.id?.value ?? `row-${byId.size}`, grant);
    totalPages = page.headers.totalPages;
    onProgress?.(byId.size, page.headers.totalElements);
    pageIndex += 1;
  }
  return [...byId.values()].filter((grant) => keptInExport(scope, grant));
}

/**
 * The people the grants name, grantees and grantors, from the directory: found, or `missing` or
 * `unauthorized` as the store records it. A lookup that fails leaves its people "Couldn't load this
 * person" rather than failing the file.
 */
async function readPeople(
  dispatch: AppDispatch,
  getState: () => RootState,
  grants: readonly Permission[],
): Promise<Pick<GrantExportNames, "people" | "emails">> {
  const userIds = new Set<string>();
  for (const grant of grants) {
    userIds.add(grant.granteeUserId.value);
    if (!grant.grantorUserId.isEqualTo(Guid.EMPTY)) userIds.add(grant.grantorUserId.value);
  }
  const found: Individual[] = [];
  let failed = false;
  for (const ids of chunks([...userIds], LOOKUP_SIZE)) {
    const answer = await dispatch(
      individualsApi.endpoints.getIndividualsByUserIds.initiate({ userIds: ids }, { subscribe: false }),
    );
    if (answer.error) failed = true;
    found.push(...(Individual.fromJSONArray(answer.data, { allowEmpty: true }) ?? []));
  }
  const people = personNameStates(userIds, found, getIndividualUnavailabilityByUserIds(getState()), failed);
  const emails = new Map<string, string>();
  for (const individual of found) {
    if (individual.userId && individual.email) emails.set(individual.userId.value.toLowerCase(), individual.email.value);
  }
  return { people, emails };
}

/** The records the per-record grants are on, by name, from each record's own centre. */
async function readTargets(
  dispatch: AppDispatch,
  kinds: readonly PermissionKind[],
  grants: readonly Permission[],
): Promise<ReadonlyMap<string, NameState>> {
  const idsByType = new Map<string, Set<string>>();
  for (const grant of grants) {
    const kind = kindOfResource(kinds, grant.resource);
    if (!kind?.perRecord || !canNameTargetsOf(kind.resourceType)) continue;
    const targetId = targetIdOf(kind, grant);
    if (!targetId) continue;
    if (!idsByType.has(kind.resourceType)) idsByType.set(kind.resourceType, new Set());
    idsByType.get(kind.resourceType)!.add(targetId);
  }

  const targets = new Map<string, NameState>();
  for (const [resourceType, ids] of idsByType) {
    for (const chunk of chunks([...ids], LOOKUP_SIZE)) {
      const answer = await dispatch(
        permissionTargetsApi.endpoints.getPermissionTargetsByIds.initiate({ resourceType, ids: chunk }, { subscribe: false }),
      );
      const states = nameStates(chunk, { data: answer.data, isError: answer.isError || !answer.data });
      for (const [id, state] of states) targets.set(targetKey(resourceType, id), state);
    }
  }
  return targets;
}

export type GrantExport = Readonly<{ grants: readonly Permission[]; names: GrantExportNames }>;

/**
 * Reads every grant the scope names, and the names its rows need. A failure to read the grants fails
 * the export, and the button says so; a failure to name some of them does not, and their cells say
 * "Couldn't load this …", as the rows would.
 */
export function readGrantExport(scope: GrantExportScope, kinds: readonly PermissionKind[], onProgress?: GrantExportProgress) {
  return async (dispatch: AppDispatch, getState: () => RootState): Promise<GrantExport> => {
    const grants = await readEveryGrant(dispatch, scope, onProgress);
    const [people, targets] = await Promise.all([readPeople(dispatch, getState, grants), readTargets(dispatch, kinds, grants)]);
    return { grants, names: { ...people, targets } };
  };
}
